import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Text,
  Badge,
  Flex,
  Button,
  HStack,
  Select,
  Stack,
} from '@chakra-ui/react';
import moment from 'moment';
import { useData } from '../../../data';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/header/Header';

const ProfileReports = () => {
  const itemsPerPage = 15;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading, refetch } = useData(
    `admins/user-reports?page_no=${currentPage}&page_size=${itemsPerPage}&status=${statusFilter}`,
  );

  const [profileReports, setProfileReports] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (data) {
      setProfileReports(data?.data?.userReports || []);
      setTotalPages(data?.data?.pagination?.total_pages || 0);
    }
  }, [data]);

  const columns = [
    {
      header: 'Reported User',
      accessorFn: (row) => `${row.user?.first_name} ${row.user?.last_name}`,
    },
    {
      header: 'Reporting User',
      accessorFn: (row) =>
        `${row.reporter?.first_name} ${row.reporter?.last_name}`,
    },
    {
      header: 'Reason',
      accessorKey: 'reason',
    },
    {
      header: 'Comment',
      accessorKey: 'details',
      cell: (info) => info.getValue() || 'No comment provided',
    },
    {
      header: 'Date/Time',
      accessorKey: 'createdAt',
      cell: (info) => moment(info.getValue()).format('MMM DD, YYYY HH:mm'),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (info) => {
        const status = info.getValue();
        const statusColors = {
          New: 'blue',
          'Under Review': 'yellow',
          Resolved: 'green',
          Dismissed: 'gray',
        };
        return (
          <Badge colorScheme={statusColors[status] || 'gray'}>{status}</Badge>
        );
      },
    },
  ];

  console.log('data', data);
  console.log('profileReports', profileReports);
  console.log('total page', totalPages);

  const table = useReactTable({
    data: profileReports || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = () => {
    setCurrentPage(1); // Reset to first page when filters change
    refetch();
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'New', label: 'New' },
    { value: 'Under Review', label: 'Under Review' },
    { value: 'Resolved', label: 'Resolved' },
    { value: 'Dismissed', label: 'Dismissed' },
  ];

  const handleRowClick = (report) => {
    navigate(`/profile-reports-details?id=${report.id}`);
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box>
      <Header title="Reported Profiles" />
      <Box p={4}>
        <Text fontSize="2xl" fontWeight="bold" mb={4}>
          Reported Profiles
        </Text>

        {/* Filter Controls */}
        <Stack direction={{ base: 'column', md: 'row' }} spacing={4} mb={4}>
          <Select
            placeholder="Filter by Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              handleFilterChange();
            }}
            maxW="200px"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Stack>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  onClick={() => handleRowClick(row.original)}
                  cursor="pointer"
                  _hover={{ bg: 'gray.50' }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        <Flex justify="center" mt={4} gap={2}>
          <Button
            leftIcon={<ChevronLeftIcon />}
            onClick={() => handlePageChange(currentPage - 1)}
            isDisabled={currentPage === 1}
            size="sm"
          >
            Previous
          </Button>

          <HStack spacing={2}>
            {[...Array(totalPages)].map((_, index) => (
              <Button
                key={index + 1}
                size="sm"
                colorScheme={currentPage === index + 1 ? 'blue' : 'gray'}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </Button>
            ))}
          </HStack>

          <Button
            rightIcon={<ChevronRightIcon />}
            onClick={() => handlePageChange(currentPage + 1)}
            isDisabled={totalPages <= currentPage}
            size="sm"
          >
            Next
          </Button>
        </Flex>

        {/* Page Info */}
        <Text textAlign="center" mt={2} color="gray.500">
          Page {currentPage} of {totalPages} • Showing {itemsPerPage} items per
          page
        </Text>
      </Box>
    </Box>
  );
};

export default ProfileReports;
